// src/components/Drivers/ViewDriverDialog.js

import React, { useMemo } from "react";
import {
  Modal,
  Tag,
  Tabs,
  Card,
  Row,
  Col,
  Typography,
  Divider,
  Tooltip,
  Progress,
  Avatar,
} from "antd";
import {
  UserOutlined,
  IdcardOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  CarOutlined,
  SafetyCertificateOutlined,
  HeartOutlined,
  TeamOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ManOutlined,
  WomanOutlined,
} from "@ant-design/icons";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import "./ViewDriverDialog.css";
import { VENCIMIENTOS_CONDUCTOR } from "./Form/VencimientosDocumentosInfo";
import { textoSiNoSinDefinir } from "../../common/SiNoSinDefinir";

const { Text, Title } = Typography;

const ViewDriverDialog = ({ driver, open, handleClose }) => {
  // Documentos con vencimiento - debe estar antes de cualquier return condicional
  const documentosVencimiento = useMemo(() => {
    if (!driver) return [];
    return [
      { key: "licencia", label: "Licencia de Conducción", fecha: driver.fecha_vencimiento_licencia },
      { key: "arl", label: "ARL", fecha: driver.fecha_vigencia_arl },
      ...VENCIMIENTOS_CONDUCTOR.map(({ campo, nombre }) => ({ key: campo, label: nombre, fecha: driver[campo] })),
    ];
  }, [driver]);

  // Early return después de los hooks
  if (!driver) return null;

  // Función para formatear fecha
  const formatDate = (date) => {
    if (!date) return "No registrada";
    return dayjs(date).format("DD/MM/YYYY");
  };

  // Función para verificar si una fecha está vencida
  const isExpired = (date) => {
    if (!date) return false;
    return dayjs(date).isBefore(dayjs(), "day");
  };

  // Función para verificar si está próximo a vencer (30 días)
  const isNearExpiry = (date) => {
    if (!date) return false;
    const daysUntilExpiry = dayjs(date).diff(dayjs(), "day");
    return daysUntilExpiry >= 0 && daysUntilExpiry <= 30;
  };

  // Calcular días restantes
  const getDaysRemaining = (date) => {
    if (!date) return null;
    return dayjs(date).diff(dayjs(), "day");
  };

  // Calcular edad
  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    return dayjs().diff(dayjs(birthDate), "year");
  };

  // Obtener color del estado
  const getEstadoColor = (estado) => {
    const colorMap = {
      activo: "green",
      inactivo: "orange",
      suspendido: "red",
    };
    return colorMap[estado?.toLowerCase()] || "default";
  };

  // Contar documentos vencidos
  const documentosVencidos = documentosVencimiento.filter(doc => isExpired(doc.fecha)).length;
  const documentosProximos = documentosVencimiento.filter(doc => isNearExpiry(doc.fecha)).length;

  // Renderizar item de información
  const InfoItem = ({ icon, label, value, fullWidth = false }) => (
    <Col xs={24} sm={fullWidth ? 24 : 12} md={fullWidth ? 24 : 8}>
      <div className="view-driver-info-item">
        <div className="view-driver-info-icon">{icon}</div>
        <div className="view-driver-info-content">
          <Text type="secondary" className="view-driver-info-label">{label}</Text>
          <Text strong className="view-driver-info-value">{value || "No registrado"}</Text>
        </div>
      </div>
    </Col>
  );

  // Renderizar documento con estado
  const DocumentItem = ({ label, fecha, extraInfo }) => {
    const expired = isExpired(fecha);
    const nearExpiry = isNearExpiry(fecha);
    const daysRemaining = getDaysRemaining(fecha);

    let statusIcon, statusColor, statusText;
    if (!fecha) {
      statusIcon = <ClockCircleOutlined />;
      statusColor = "#8c8c8c";
      statusText = "Sin fecha";
    } else if (expired) {
      statusIcon = <WarningOutlined />;
      statusColor = "#ff4d4f";
      statusText = `Vencido hace ${Math.abs(daysRemaining)} días`;
    } else if (nearExpiry) {
      statusIcon = <WarningOutlined />;
      statusColor = "#faad14";
      statusText = `Vence en ${daysRemaining} días`;
    } else {
      statusIcon = <CheckCircleOutlined />;
      statusColor = "#52c41a";
      statusText = `${daysRemaining} días restantes`;
    }

    return (
      <div className="view-driver-doc-item">
        <div className="view-driver-doc-header">
          <Text strong>{label}</Text>
          <Tooltip title={statusText}>
            <span style={{ color: statusColor }}>{statusIcon}</span>
          </Tooltip>
        </div>
        {extraInfo && <Text type="secondary" style={{ fontSize: 12 }}>{extraInfo}</Text>}
        <Text type="secondary">{formatDate(fecha)}</Text>
        {fecha && (
          <Progress
            percent={expired ? 100 : Math.max(0, 100 - (daysRemaining / 365) * 100)}
            size="small"
            status={expired ? "exception" : nearExpiry ? "active" : "success"}
            showInfo={false}
            strokeColor={statusColor}
          />
        )}
      </div>
    );
  };

  // Tab: Información Personal
  const PersonalInfoTab = () => (
    <div className="view-driver-tab-content">
      <Card className="view-driver-card">
        <div className="view-driver-header-card">
          <Avatar
            size={80}
            className="view-driver-avatar"
            icon={<UserOutlined />}
            style={{
              backgroundColor: driver.genero?.toLowerCase() === 'femenino' ? '#eb2f96' : '#1890ff',
            }}
          />
          <div className="view-driver-header-info">
            <Title level={4} style={{ margin: 0 }}>{driver.nombre}</Title>
            <Text type="secondary">
              {driver.tipoDocumento?.descripcion || "Documento"}: {driver.numero_documento}
            </Text>
            <div style={{ marginTop: 8 }}>
              <Tag color={getEstadoColor(driver.estado)} style={{ fontSize: 14, padding: "4px 12px" }}>
                {driver.estado}
              </Tag>
              {driver.genera_fuec === false && <Tag color="orange">No genera FUEC</Tag>}
              {driver.genera_preoperacional === false && <Tag color="orange">No genera preoperacional</Tag>}
              {documentosVencidos > 0 && (
                <Tag color="error" icon={<WarningOutlined />}>
                  {documentosVencidos} doc. vencidos
                </Tag>
              )}
              {documentosProximos > 0 && (
                <Tag color="warning" icon={<ClockCircleOutlined />}>
                  {documentosProximos} por vencer
                </Tag>
              )}
            </div>
          </div>
        </div>
      </Card>

      <Divider orientation="left">Datos Personales</Divider>
      <Row gutter={[16, 16]}>
        <InfoItem icon={<UserOutlined />} label="Nombre Completo" value={driver.nombre} fullWidth />
        <InfoItem icon={<IdcardOutlined />} label="Tipo Documento" value={driver.tipoDocumento?.descripcion} />
        <InfoItem icon={<IdcardOutlined />} label="N° Documento" value={driver.numero_documento} />
        <InfoItem
          icon={driver.genero?.toLowerCase() === 'femenino' ? <WomanOutlined /> : <ManOutlined />}
          label="Género"
          value={driver.genero}
        />
        <InfoItem
          icon={<CalendarOutlined />}
          label="Fecha Nacimiento"
          value={`${formatDate(driver.fecha_nacimiento)}${calculateAge(driver.fecha_nacimiento) ? ` (${calculateAge(driver.fecha_nacimiento)} años)` : ''}`}
        />
        <InfoItem icon={<HeartOutlined />} label="Grupo Sanguíneo" value={driver.grupo_sanguineo} />
        <InfoItem icon={<PhoneOutlined />} label="Teléfono" value={driver.telefono} />
        <InfoItem icon={<MailOutlined />} label="Correo" value={driver.correo} />
      </Row>
    </div>
  );

  // Tab: Dirección
  const AddressInfoTab = () => (
    <div className="view-driver-tab-content">
      <Card className="view-driver-card">
        <div className="view-driver-section-header">
          <EnvironmentOutlined className="view-driver-section-icon" />
          <Title level={5} style={{ margin: 0 }}>Información de Ubicación</Title>
        </div>
      </Card>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <InfoItem icon={<EnvironmentOutlined />} label="Dirección" value={driver.direccion} fullWidth />
        <InfoItem icon={<EnvironmentOutlined />} label="Barrio" value={driver.barrio} />
        <InfoItem icon={<EnvironmentOutlined />} label="Localidad" value={driver.localidad} />
        <InfoItem icon={<EnvironmentOutlined />} label="Ciudad" value={driver.ciudad?.nombre || driver.ciudad} />
        <InfoItem icon={<EnvironmentOutlined />} label="Departamento" value={driver.departamento?.nombre || driver.departamento} />
        <InfoItem icon={<EnvironmentOutlined />} label="Zona Asignada" value={driver.zona?.nombre} />
        {driver.coordenadas && (
          <InfoItem icon={<EnvironmentOutlined />} label="Coordenadas" value={
            typeof driver.coordenadas === 'object' && driver.coordenadas.coordinates
              ? `${driver.coordenadas.coordinates[1]}, ${driver.coordenadas.coordinates[0]}`
              : driver.coordenadas
          } />
        )}
      </Row>
    </div>
  );

  // Tab: Licencia
  const LicenseInfoTab = () => (
    <div className="view-driver-tab-content">
      <Card className="view-driver-card">
        <div className="view-driver-section-header">
          <CarOutlined className="view-driver-section-icon" />
          <Title level={5} style={{ margin: 0 }}>Licencia de Conducción</Title>
        </div>
        {isExpired(driver.fecha_vencimiento_licencia) && (
          <Tag color="error" icon={<WarningOutlined />} style={{ marginTop: 12 }}>
            Licencia vencida
          </Tag>
        )}
        {isNearExpiry(driver.fecha_vencimiento_licencia) && (
          <Tag color="warning" icon={<ClockCircleOutlined />} style={{ marginTop: 12 }}>
            Licencia próxima a vencer
          </Tag>
        )}
      </Card>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <InfoItem icon={<IdcardOutlined />} label="N° Licencia" value={driver.licencia_conduccion} />
        <InfoItem icon={<CarOutlined />} label="Categoría" value={driver.categoria_licencia} />
        <Col xs={24} sm={12} md={8}>
          <Card size="small" className="view-driver-doc-card">
            <DocumentItem
              label="Vencimiento Licencia"
              fecha={driver.fecha_vencimiento_licencia}
              extraInfo={driver.categoria_licencia ? `Categoría: ${driver.categoria_licencia}` : null}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );

  // Tab: ARL y Laboral
  const LaborInfoTab = () => (
    <div className="view-driver-tab-content">
      <Card className="view-driver-card">
        <div className="view-driver-section-header">
          <SafetyCertificateOutlined className="view-driver-section-icon" />
          <Title level={5} style={{ margin: 0 }}>Información Laboral y ARL</Title>
        </div>
      </Card>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <InfoItem icon={<CalendarOutlined />} label="Fecha de Ingreso" value={formatDate(driver.fecha_ingreso)} />
        <InfoItem icon={<UserOutlined />} label="Conductor propio / interno" value={textoSiNoSinDefinir(driver.conductor_propio)} />
        <InfoItem icon={<CalendarOutlined />} label="Fecha Fin Contrato" value={formatDate(driver.fecha_fin_contrato)} />
        <InfoItem icon={<SafetyCertificateOutlined />} label="ARL" value={driver.arl} />
        <Col xs={24} sm={12} md={8}>
          <Card size="small" className="view-driver-doc-card">
            <DocumentItem
              label="Vigencia ARL"
              fecha={driver.fecha_vigencia_arl}
              extraInfo={driver.arl || null}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );

  // Tab: Contacto Emergencia
  const EmergencyInfoTab = () => (
    <div className="view-driver-tab-content">
      <Card className="view-driver-card">
        <div className="view-driver-section-header">
          <TeamOutlined className="view-driver-section-icon" />
          <Title level={5} style={{ margin: 0 }}>Contacto de Emergencia</Title>
        </div>
      </Card>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <InfoItem icon={<UserOutlined />} label="Nombre" value={driver.nombre_emergencia} />
        <InfoItem icon={<PhoneOutlined />} label="Teléfono" value={driver.numero_emergencia} />
        <InfoItem icon={<TeamOutlined />} label="Parentesco" value={driver.parentesco_emergencia} />
      </Row>
    </div>
  );

  // Tab: Documentos (resumen)
  const DocumentsInfoTab = () => (
    <div className="view-driver-tab-content">
      <Card className="view-driver-card">
        <div className="view-driver-section-header">
          <SafetyCertificateOutlined className="view-driver-section-icon" />
          <Title level={5} style={{ margin: 0 }}>Estado de Documentos</Title>
        </div>
        {(documentosVencidos > 0 || documentosProximos > 0) && (
          <div style={{ marginTop: 12 }}>
            {documentosVencidos > 0 && (
              <Tag color="error" icon={<WarningOutlined />} style={{ marginBottom: 4 }}>
                {documentosVencidos} documento(s) vencido(s)
              </Tag>
            )}
            {documentosProximos > 0 && (
              <Tag color="warning" icon={<ClockCircleOutlined />}>
                {documentosProximos} documento(s) próximo(s) a vencer
              </Tag>
            )}
          </div>
        )}
      </Card>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {documentosVencimiento.map((doc) => (
          <Col xs={24} sm={12} key={doc.key}>
            <Card size="small" className="view-driver-doc-card">
              <DocumentItem label={doc.label} fecha={doc.fecha} />
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );

  const tabItems = [
    {
      key: "personal",
      label: (
        <span>
          <UserOutlined /> Personal
        </span>
      ),
      children: <PersonalInfoTab />,
    },
    {
      key: "direccion",
      label: (
        <span>
          <EnvironmentOutlined /> Dirección
        </span>
      ),
      children: <AddressInfoTab />,
    },
    {
      key: "licencia",
      label: (
        <span>
          <CarOutlined /> Licencia
          {isExpired(driver.fecha_vencimiento_licencia) && (
            <Tag color="error" style={{ marginLeft: 8, fontSize: 10 }}>!</Tag>
          )}
        </span>
      ),
      children: <LicenseInfoTab />,
    },
    {
      key: "laboral",
      label: (
        <span>
          <SafetyCertificateOutlined /> Laboral
        </span>
      ),
      children: <LaborInfoTab />,
    },
    {
      key: "emergencia",
      label: (
        <span>
          <TeamOutlined /> Emergencia
        </span>
      ),
      children: <EmergencyInfoTab />,
    },
    {
      key: "documentos",
      label: (
        <span>
          <SafetyCertificateOutlined /> Documentos
          {documentosVencidos > 0 && (
            <Tag color="error" style={{ marginLeft: 8, fontSize: 10 }}>{documentosVencidos}</Tag>
          )}
        </span>
      ),
      children: <DocumentsInfoTab />,
    },
  ];

  return (
    <Modal
      title={
        <div className="view-driver-modal-header">
          <UserOutlined className="view-driver-modal-icon" />
          <span>Detalles del Conductor</span>
        </div>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={900}
      className="view-driver-modal"
      centered
    >
      <Tabs
        defaultActiveKey="personal"
        items={tabItems}
        className="view-driver-tabs"
      />
    </Modal>
  );
};

ViewDriverDialog.propTypes = {
  driver: PropTypes.object,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default ViewDriverDialog;
